import React, { useCallback, useState } from 'react';
import { CloudUpload, X, Image as ImageIcon, Eye } from 'lucide-react';
import ViewDocumentModal, { DocumentItem } from './ViewDocumentModal';

interface MultiImageUploaderProps {
    value: (File | string)[];
    onChange: (value: (File | string)[]) => void;
    label?: string;
    error?: string;
    maxFiles?: number;
    type?: string;
}

const MultiImageUploader: React.FC<MultiImageUploaderProps> = ({
    value = [],
    onChange,
    label = "Product Images",
    error,
    maxFiles = 5,
    type = ".jpg,.jpeg,.png,.webp,.mp4,.pdf,.txt,.rtf"
}) => {
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerIndex, setViewerIndex] = useState(0);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            let updatedValue;
            if (maxFiles === 1) {
                updatedValue = [newFiles[newFiles.length - 1]];
            } else {
                updatedValue = [...value, ...newFiles].slice(0, maxFiles);
            }
            onChange(updatedValue);
        }
        e.target.value = null;
    };

    const removeImage = (index: number) => {
        const newValue = value.filter((_, i) => i !== index);
        onChange(newValue);
    };

    const onDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const newFiles = Array.from(e.dataTransfer.files);
            let updatedValue;
            if (maxFiles === 1) {
                updatedValue = [newFiles[newFiles.length - 1]];
            } else {
                updatedValue = [...value, ...newFiles].slice(0, maxFiles);
            }
            onChange(updatedValue);
            e.dataTransfer.clearData();
        }
    }, [value, onChange, maxFiles]);

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    // Map value items to DocumentItems for the viewer modal
    const docItems: DocumentItem[] = value.map((item, i) => {
        const url = typeof item === 'string' ? item : URL.createObjectURL(item);
        const name = typeof item === 'string' ? item.split('/').pop() || `File ${i + 1}` : item.name;
        const extension = name.split('.').pop()?.toLowerCase();
        return {
            id: String(i),
            url: url,
            label: name,
            subtitle: label || 'Uploaded File',
            extension: extension,
        };
    });

    return (
        <div className="space-y-3">
            {label && (
                <label className="block text-sm font-semibold text-foreground tracking-tight">
                    {label} <span className="text-xs font-normal text-muted-foreground ml-1">(Up to {maxFiles} images)</span>
                </label>
            )}

            <div
                onDrop={onDrop}
                onDragOver={onDragOver}
                className={`relative group border-2 border-dashed rounded-3xl p-10 transition-all duration-300 flex flex-col items-center justify-center gap-4 cursor-pointer mt-2
                    ${error ? 'border-destructive bg-destructive/5' : 'border-secondary/60 bg-secondary/5 hover:border-primary/50 hover:bg-primary/5'}`}
            >
                <input
                    type="file"
                    multiple
                    accept={type}
                    onChange={handleFileChange}
                    className="absolute inset-0 opacity-0 cursor-pointer z-20"
                />

                <div className="relative">
                    <div className="p-5 rounded-2xl bg-background shadow-sm group-hover:shadow-md group-hover:-translate-y-1 transition-all duration-300 border border-secondary/20">
                        <CloudUpload className="w-10 h-10 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                </div>

                <div className="text-center space-y-1">
                    <p className="text-sm font-semibold text-foreground tracking-tight">
                        <span className="text-primary hover:underline transition-all">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-muted-foreground font-medium">
                        {type?.split(',').map(t => t.trim().replace(/\./g, '').toUpperCase()).join(', ') || 'ANY FORMAT'}
                    </p>
                </div>

                {/* Subtle highlight effect */}
                <div className="absolute inset-0 rounded-3xl bg-primary/0 group-hover:bg-primary/[0.02] transition-colors pointer-events-none" />
            </div>

            {error && <p className="text-xs text-destructive font-medium">{error}</p>}

            {value.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
                    {docItems.map((doc, index) => {
                        const isNew = typeof value[index] !== 'string';
                        return (
                            <div
                                key={index}
                                onClick={() => {
                                    setViewerIndex(index);
                                    setViewerOpen(true);
                                }}
                                className="relative group aspect-square rounded-xl overflow-hidden border bg-secondary/5 shadow-sm hover:shadow-md transition-all cursor-zoom-in"
                            >
                                <img
                                    src={doc.url}
                                    alt={doc.label}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />

                                {/* Hover overlay — eye icon */}
                                <div className="absolute inset-0 bg-black/25 transition-colors flex items-center justify-center pointer-events-none">
                                    <div className="opacity-100 transition-opacity sm:bg-white/90 rounded-full p-2 shadow-md">
                                        <Eye className="w-4 h-4 text-primary" />
                                    </div>
                                </div>

                                {/* Remove button container */}
                                <div
                                    className="absolute top-1.5 right-1.5 opacity-100 transition-opacity z-10"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <button
                                        type="button"
                                        onClick={() => removeImage(index)}
                                        className="p-1.5 rounded-full bg-destructive/90 text-destructive-foreground hover:bg-destructive hover:scale-110 transition-all shadow-md backdrop-blur-sm cursor-pointer"
                                        title="Remove image"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                                {isNew && (
                                    <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-black/60 text-[8px] font-bold text-white uppercase tracking-wider backdrop-blur-sm">
                                        New
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {value.length > 0 && (
                <ViewDocumentModal
                    open={viewerOpen}
                    onOpenChange={setViewerOpen}
                    documents={docItems}
                    initialIndex={viewerIndex}
                />
            )}
        </div>
    );
};

export default MultiImageUploader;
